import { DefaultItem } from "@directus/sdk";
import { Favorite, FavoriteBorder } from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";
import { format } from "date-fns";
import * as React from "react";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";

import { Asset as GqlAsset } from "../../generated/gql/graphql";
import { AssetListContext } from "../../providers/asset-list-provider";
import { CategoryContext } from "../../providers/category-provider";
import { useDirectus } from "../../providers/DirectusProvider";
import Asset from "../../services/asset";
import { ConfirmationModalType } from "../../types/confirm-modal";
import { findRootCategoryById } from "../../utils";
import { getLatestDate } from "../../utils/date";
import { Resolution } from "../card/card-box";
import Dropdown from "../dropdown/dropdown";
import MediaTypeIcon from "../media-format/media-type-icon";

type Props = {
  classes?: string;
  draggable?: boolean;
  isCheckbox?: boolean;
  asset: GqlAsset;
  offset: number;
  isCollection?: boolean;
  getDragId?: number | undefined | string;
  setDragId?: React.Dispatch<React.SetStateAction<number | undefined | string>>;
  setConfirm?: (val: ConfirmationModalType) => void;
};

export default function ListItem(props: Props): React.ReactElement {
  const {
    asset,
    offset,
    draggable,
    isCollection,
    getDragId,
    setDragId,
    setConfirm,
  } = props;

  const [type, setType] = useState<string | undefined>();
  const [resolution, setResolution] = useState<Resolution>();
  const [stackVersions, setStackVersions] = useState<DefaultItem<GqlAsset>[]>(
    []
  );
  const { directus } = useDirectus();

  const { setFavorite, addAssetToStackVersion, getFileType } = Asset(directus);
  const { categories } = useContext(CategoryContext);
  const { selectedFiles, setSelectedFile } = useContext(AssetListContext);
  const isSelected = selectedFiles.find((v) => v.id === asset.id) !== undefined;

  React.useEffect(() => {
    (async (): Promise<void> => {
      if (!asset.image) {
        return;
      }

      const file = await getFileType(asset.image);

      setType(file?.type);
      setResolution({
        width: file?.width ?? 0,
        height: file?.height ?? 0,
      });
    })();
  }, []);

  // useEffect(() => {
  //     (async () => {
  //         const file = await getFileType(asset.image || "");
  //         // setType(file?.type);
  //         // setStackVersions(await getAssetStackVersions(asset.id || '') ?? []);
  //     })();
  // }, []);

  const capitalizeFirstLetter = (string: string): string => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>): void => {
    if (setDragId) {
      setDragId(parseInt(event.currentTarget.id));
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.currentTarget.style.background = "";
  };

  const handleDragEnd = (event: React.DragEvent<HTMLDivElement>): void => {
    event.currentTarget.style.background = "";
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();

    if (parseInt(event.currentTarget.id) != getDragId) {
      event.currentTarget.style.background = "#d3d3d366";
    }
  };

  const handleDrop = async (
    event: React.DragEvent<HTMLDivElement>,
    asset: GqlAsset
  ): Promise<void> => {
    if (!asset.id) {
      return;
    }

    event.preventDefault();
    event.currentTarget.style.background = "";

    if (getDragId && getDragId !== asset.id) {
      await addAssetToStackVersion(asset.id, getDragId);

      // ToDo: add stack versions
      // setStackVersions(await getAssetStackVersions(asset.id) ?? []);
      // setAssets(assets?.filter(file => file.id !== getDradId));
    }
  };

  return (
    <div
      id={asset.id || ""}
      draggable={!stackVersions.length ? draggable : false}
      onDragStart={(e) => handleDragStart(e)}
      onDragLeave={(e) => handleDragLeave(e)}
      onDragEnd={(e) => handleDragEnd(e)}
      onDragOver={(e) => handleDragOver(e)}
      onDrop={(e) => handleDrop(e, asset)}
      key={asset.id}
      style={{ borderColor: "#E8E8E8" }}
      className={`
                ${!stackVersions.length ? "cursor-grabbing" : ""}
                flex shadow-none rounded-[15px] border-solid border-1 w-full p-[10px] text-[14px] items-center mb-20
            `}
    >
      <div className="flex-1">
        <div className="flex items-center">
          <div className="flex w-[40px] flex-col">
            <Checkbox
              key={asset.id}
              id={asset.id || ""}
              onChange={() => setSelectedFile(asset, !isSelected)}
              value={asset.id}
              checked={isSelected}
              sx={{ "& .MuiSvgIcon-root": { fontSize: 22 } }}
            />
            <Checkbox
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFavorite(asset.id || "", event.target.checked)
              }
              defaultChecked={asset.favorite || false}
              icon={<FavoriteBorder />}
              checkedIcon={<Favorite />}
            />
          </div>
          {!!stackVersions.length && (
            <span className="font-bold">v. {stackVersions.length + 1}</span>
          )}
          <Link to={`/file/${asset.id}/${offset}`}>
            <MediaTypeIcon
              asset={asset}
              classes={`text-[50px] max-w-[50px] max-h-[50px] object-contain ${
                stackVersions.length ? "ml-[20px]" : "ml-[42px]"
              }`}
            />
          </Link>
        </div>
      </div>
      <div className="flex-1 pr-10">{asset.name}</div>
      <div className="flex-1">
        {findRootCategoryById(asset.categoryId || "", categories)}
      </div>
      <div className="flex-1">
        {capitalizeFirstLetter((asset.assetTypes || "").replace("_", " "))}
      </div>
      <div className="flex-1">
        <div className="flex justify-between items-center">
          {asset &&
            asset.dateCreated &&
            format(getLatestDate(asset) || 0, "dd-MM-yyyy")}
          <Dropdown
            asset={asset}
            fileType={type}
            resolution={resolution}
            onConfirm={setConfirm}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            stackVersions={stackVersions}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            setStackVersions={setStackVersions}
            setCollectionItem={isCollection}
          />
        </div>
      </div>
    </div>
  );
}
